import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, Filter, X } from 'lucide-react';

const SearchFilter = ({ searchQuery, setSearchQuery, priceFilter, setPriceFilter, yearFilter, setYearFilter }) => {
    const [isYearFilterOpen, setIsYearFilterOpen] = useState(false);
    const [isPriceFilterOpen, setIsPriceFilterOpen] = useState(false);
    const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    // Создаем массив годов от 1990 до текущего года
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: currentYear - 1989 }, (_, i) => currentYear - i);

    const yearFilterRef = useRef(null);
    const priceFilterRef = useRef(null);

    // Состояние для отображения активных фильтров
    const hasActiveFilters = priceFilter !== 'all' ||
        (yearFilter && yearFilter.minYear !== 2000) ||
        (yearFilter && yearFilter.maxYear !== currentYear);

    // Отслеживаем размер экрана
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Закрываем меню при клике вне его области
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isYearFilterOpen && yearFilterRef.current && !yearFilterRef.current.contains(event.target)) {
                setIsYearFilterOpen(false);
            }

            if (isPriceFilterOpen && priceFilterRef.current && !priceFilterRef.current.contains(event.target)) {
                setIsPriceFilterOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isYearFilterOpen, isPriceFilterOpen]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handlePriceChange = (value) => {
        setPriceFilter(value);
        setIsPriceFilterOpen(false);
    };

    // Обработчик изменения года через выпадающий список
    const handleYearChange = (field, value) => {
        const numValue = Number(value);
        console.log(`Changing ${field} to:`, numValue); // Отладочный вывод

        // Убедимся, что значение корректное
        if (!isNaN(numValue)) {
            const newYearFilter = {
                ...yearFilter,
                [field]: numValue
            };
            console.log('New year filter:', newYearFilter); // Отладочный вывод
            setYearFilter(newYearFilter);
        }
    };

    // Функция для отображения текста фильтра цены
    const getPriceFilterText = () => {
        switch (priceFilter) {
            case 'all': return 'Все цены';
            case 'under1m': return 'До 1 млн ₽';
            case 'under1.5m': return 'До 1.5 млн ₽';
            case 'under2m': return 'До 2 млн ₽';
            case 'under2.5m': return 'До 2.5 млн ₽';
            case 'under3m': return 'До 3 млн ₽';
            default: return 'Все цены';
        }
    };

    // Функция для отображения текста фильтра года
    const getYearFilterText = () => {
        // Используем безопасное обращение к свойствам
        const minYearDisplay = yearFilter?.minYear || 2000;
        const maxYearDisplay = yearFilter?.maxYear || currentYear;
        return `Год: ${minYearDisplay} – ${maxYearDisplay}`;
    };

    // Функция для сброса фильтров
    const resetFilters = () => {
        setPriceFilter('all');
        // Устанавливаем значения по умолчанию
        setYearFilter({ minYear: 2000, maxYear: currentYear });
        setSearchQuery('');
    };

    return (
        <div className="search-section">
            {isMobile ? (
                // Мобильная версия
                <>
                    <div className="search-container-mobile">
                        <div className="search-input-wrapper">
                            <Search size={22} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Поиск по марке или модели"
                                className="search-input"
                                value={searchQuery || ''}
                                onChange={handleSearchChange}
                            />
                        </div>

                        <button
                            className={`filter-toggle-button ${hasActiveFilters ? 'has-active-filters' : ''}`}
                            onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
                        >
                            <Filter size={18} />
                            <span>Фильтры</span>
                            {hasActiveFilters && <span className="filter-badge"></span>}
                        </button>
                    </div>

                    {isMobileFiltersOpen && (
                        <div className="mobile-filters-panel">
                            <div className="mobile-filters-header">
                                <h3>Фильтры</h3>
                                <button
                                    className="close-filters-button"
                                    onClick={() => setIsMobileFiltersOpen(false)}
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="mobile-filters-content">
                                <div className="filter-group">
                                    <label>Цена</label>
                                    <div className="mobile-price-options">
                                        {[
                                            { value: 'all', label: 'Все цены' },
                                            { value: 'under1m', label: 'До 1 млн ₽' },
                                            { value: 'under1.5m', label: 'До 1.5 млн ₽' },
                                            { value: 'under2m', label: 'До 2 млн ₽' },
                                            { value: 'under2.5m', label: 'До 2.5 млн ₽' },
                                            { value: 'under3m', label: 'До 3 млн ₽' }
                                        ].map(option => (
                                            <button
                                                key={option.value}
                                                className={`price-option ${priceFilter === option.value ? 'active' : ''}`}
                                                onClick={() => handlePriceChange(option.value)}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="filter-group">
                                    <label>Год выпуска</label>
                                    <div className="year-filter-inputs">
                                        <div className="year-input-group">
                                            <label>От</label>
                                            <select
                                                className="year-filter"
                                                value={yearFilter?.minYear || 2000}
                                                onChange={(e) => handleYearChange('minYear', e.target.value)}
                                            >
                                                {yearOptions.map(year => (
                                                    <option key={`min-mobile-${year}`} value={year}>{year}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="year-input-group">
                                            <label>До</label>
                                            <select
                                                className="year-filter"
                                                value={yearFilter?.maxYear || currentYear}
                                                onChange={(e) => handleYearChange('maxYear', e.target.value)}
                                            >
                                                {yearOptions.map(year => (
                                                    <option key={`max-mobile-${year}`} value={year}>{year}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="filters-actions">
                                    <button
                                        className="reset-filters-button"
                                        onClick={resetFilters}
                                    >
                                        Сбросить фильтры
                                    </button>
                                    <button
                                        className="apply-filters-button"
                                        onClick={() => setIsMobileFiltersOpen(false)}
                                    >
                                        Применить
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                // Десктопная версия
                <div className="search-container">
                    <div className="search-input-wrapper">
                        <Search size={22} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Поиск по марке или модели"
                            className="search-input"
                            value={searchQuery || ''}
                            onChange={handleSearchChange}
                        />
                    </div>

                    <div ref={priceFilterRef} className="filter-dropdown price-filter-dropdown">
                        <div
                            className={`filter-header ${priceFilter !== 'all' ? 'active' : ''}`}
                            onClick={() => setIsPriceFilterOpen(!isPriceFilterOpen)}
                        >
                            <span className="filter-title">{getPriceFilterText()}</span>
                            {isPriceFilterOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </div>

                        {isPriceFilterOpen && (
                            <div className="filter-panel price-filter-panel">
                                <div className="price-options">
                                    {[
                                        { value: 'all', label: 'Все цены' },
                                        { value: 'under1m', label: 'До 1 млн ₽' },
                                        { value: 'under1.5m', label: 'До 1.5 млн ₽' },
                                        { value: 'under2m', label: 'До 2 млн ₽' },
                                        { value: 'under2.5m', label: 'До 2.5 млн ₽' },
                                        { value: 'under3m', label: 'До 3 млн ₽' }
                                    ].map(option => (
                                        <div
                                            key={option.value}
                                            className={`price-option ${priceFilter === option.value ? 'active' : ''}`}
                                            onClick={() => handlePriceChange(option.value)}
                                        >
                                            {option.label}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div ref={yearFilterRef} className="filter-dropdown year-filter-dropdown">
                        <div
                            className={`filter-header ${(yearFilter?.minYear !== 2000) || (yearFilter?.maxYear !== currentYear) ? 'active' : ''}`}
                            onClick={() => setIsYearFilterOpen(!isYearFilterOpen)}
                        >
                            <span className="filter-title">
                                {getYearFilterText()}
                            </span>
                            {isYearFilterOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </div>

                        {isYearFilterOpen && (
                            <div className="filter-panel year-filter-panel">
                                <div className="year-filter-container">
                                    <div className="year-filter-group">
                                        <label htmlFor="minYear">От</label>
                                        <select
                                            id="minYear"
                                            className="year-filter year-filter-min"
                                            value={yearFilter?.minYear || 2000}
                                            onChange={(e) => handleYearChange('minYear', e.target.value)}
                                        >
                                            {yearOptions.map(year => (
                                                <option key={`min-${year}`} value={year}>{year}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="year-filter-group">
                                        <label htmlFor="maxYear">До</label>
                                        <select
                                            id="maxYear"
                                            className="year-filter year-filter-max"
                                            value={yearFilter?.maxYear || currentYear}
                                            onChange={(e) => handleYearChange('maxYear', e.target.value)}
                                        >
                                            {yearOptions.map(year => (
                                                <option key={`max-${year}`} value={year}>{year}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {hasActiveFilters && (
                        <button
                            className="reset-filters-button"
                            onClick={resetFilters}
                        >
                            <X size={16} />
                            Сбросить
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchFilter;